import { useEffect, useState } from "react";
import { MessageCircle, Sparkles } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { WA_FLOAT } from "@/lib/constants";
import { api } from "@/lib/api";

const DEFAULTS = [
  { id: "d1", name: "Vanessa", role: "Atendimento e Recepção", kind: "human", photo: "" },
  { id: "d2", name: "Ielane", role: "Atendimento e Recepção", kind: "human", photo: "" },
  { id: "d3", name: "RAFA", role: "Assistente virtual", kind: "ai", photo: "/rafa.png" },
];

const InitialAvatar = ({ letter }) => (
  <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-cob-green to-cob-dark">
    <span className="font-serif text-6xl font-semibold text-cob-gold">{letter}</span>
  </div>
);

const HumanCard = ({ person, delay }) => (
  <Reveal delay={delay} className="h-full">
    <article
      data-testid={`attendant-card-${person.name.toLowerCase()}`}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-cob-light/70 bg-white shadow-[0_12px_40px_-22px_rgba(6,63,50,0.25)] transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_28px_55px_-24px_rgba(6,63,50,0.4)]"
    >
      <div className="relative h-56 overflow-hidden bg-cob-light/40">
        {person.photo ? (
          <img loading="lazy" decoding="async"
            src={person.photo}
            alt={person.name}
            className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]"
          />
        ) : (
          <InitialAvatar letter={(person.name || "?")[0].toUpperCase()} />
        )}
      </div>
      <div className="flex flex-1 flex-col p-6 text-center">
        <span className="mx-auto block h-px w-10 bg-cob-gold" />
        <h3 className="mt-3 font-serif text-2xl font-semibold text-cob-dark">{person.name}</h3>
        {person.role && (
          <p className="mt-1 text-[12px] font-bold uppercase tracking-[0.16em] text-cob-gold">{person.role}</p>
        )}
        <a
          href={WA_FLOAT}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`attendant-whatsapp-${person.name.toLowerCase()}`}
          className="btn-press mt-5 inline-flex items-center justify-center gap-2 rounded-full border border-cob-green/30 px-5 py-2.5 text-[13px] font-semibold text-cob-green transition-colors hover:bg-cob-green hover:text-white"
        >
          <MessageCircle className="h-4 w-4" />
          Falar pelo WhatsApp
        </a>
      </div>
    </article>
  </Reveal>
);

const AiCard = ({ person, delay }) => (
  <Reveal delay={delay} className="h-full">
    <article
      data-testid={`attendant-card-${person.name.toLowerCase()}`}
      className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-cob-gold/40 bg-cob-dark shadow-[0_12px_40px_-22px_rgba(6,63,50,0.45)] transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_28px_55px_-24px_rgba(6,63,50,0.55)]"
    >
      <div className="relative flex h-56 items-center justify-center overflow-hidden bg-gradient-to-b from-[#9fd3e8]/25 to-transparent">
        <img loading="lazy" decoding="async"
          src={person.photo || "/rafa-button.png"}
          alt={`${person.name}, assistente virtual da Clínica COB`}
          className="h-44 w-44 rounded-full object-cover ring-4 ring-cob-gold/50 shadow-[0_18px_40px_-15px_rgba(0,0,0,0.5)] transition-transform duration-500 group-hover:scale-[1.06]"
        />
        <span className="absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-cob-gold px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-cob-dark">
          <Sparkles className="h-3 w-3" /> IA
        </span>
      </div>
      <div className="flex flex-1 flex-col p-6 text-center">
        <span className="mx-auto block h-px w-10 bg-cob-gold" />
        <h3 className="mt-3 font-serif text-2xl font-semibold text-white">{person.name}</h3>
        {person.role && (
          <p className="mt-1 text-[12px] font-bold uppercase tracking-[0.16em] text-cob-gold">{person.role}</p>
        )}
        <p className="mt-2 text-[13px] leading-relaxed text-white/65">
          Tira dúvidas sobre modalidades e localização, aqui mesmo no site.
        </p>
        <button
          type="button"
          data-testid="attendant-rafa-open"
          onClick={() => window.dispatchEvent(new CustomEvent("rafa:open"))}
          className="btn-press mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-cob-gold px-5 py-2.5 text-[13px] font-bold text-cob-dark transition-colors hover:bg-white"
        >
          <Sparkles className="h-4 w-4" />
          Conversar com o {person.name}
        </button>
      </div>
    </article>
  </Reveal>
);

export const Attendants = () => {
  const [items, setItems] = useState(DEFAULTS);

  useEffect(() => {
    api
      .get("/attendants")
      .then((r) => {
        if (r.data.length > 0) setItems(r.data);
      })
      .catch(() => {});
  }, []);

  return (
    <section id="atendentes" data-testid="attendants-section" className="bg-white py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          center
          overline="Atendimento"
          title="Quem recebe você"
          text="Nossa equipe de atendimento está pronta para ajudar — pessoalmente, pelo WhatsApp ou com o RAFA, nosso assistente virtual."
        />

        <div className="mt-14 flex flex-wrap justify-center gap-8 max-w-4xl mx-auto">
          {items.map((person, i) => (
            <div key={person.id} className="w-full sm:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.4rem)] max-w-xs">
              {person.kind === "ai" ? (
                <AiCard person={person} delay={i * 0.1} />
              ) : (
                <HumanCard person={person} delay={i * 0.1} />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
